import React, { useState } from "react";
import { Button } from "antd";
import { FilePdfOutlined } from "@ant-design/icons";
import pdfMake from "pdfmake/build/pdfmake";
import "pdfmake/build/vfs_fonts";
import parser from "../../../../shared/helpers/parser";
import Utils from "../../../../shared/helpers/utils";

const bool = val => val === true ? 'Si' : 'No';
const str  = val => String(val ?? '');

// Helpers para construir bloques de sub-tablas en el PDF
const sectionTitle = (text) => ({
  text,
  bold: true,
  fontSize: 9,
  margin: [10, 8, 0, 3],
  color: '#555555',
});

const subTable = (headers, rows, widths) => ({
  margin: [10, 0, 0, 4],
  table: {
    headerRows: 1,
    widths: widths || Array(headers.length).fill('*'),
    body: [
      headers.map(h => ({ text: h, bold: true, fillColor: '#ffa940', color: '#fff', fontSize: 6 })),
      ...rows,
    ],
  },
  layout: {
    hLineWidth: () => 0.5,
    vLineWidth: () => 0.5,
    hLineColor: () => '#cccccc',
    vLineColor: () => '#cccccc',
  },
});

const ExportSustanciasPDF = ({ filteredData, sustancias, establecimientosEmpresa, unidadesMedida, anexoi, cuit }) => {
  const [loading, setLoading] = useState(false);

  const getEstablecimiento = (id) => {
    if (!anexoi?.EstablecimientosDeclarados) return null;
    return anexoi.EstablecimientosDeclarados.find(x => x.IdEstablecimientoEmpresa == id);
  };

  const downloadPDF = () => {
    setLoading(true);
    setTimeout(() => {
      const today = new Date();
      const formattedDate = today.toLocaleString(undefined, { hour12: false });

      // ---- Tabla principal de sustancias ----
      const sustanciasBody = [
        [
          { text: 'Id',                    bold: true, fillColor: '#1890ff', color: '#fff', fontSize: 7 },
          { text: 'Sustancia',             bold: true, fillColor: '#1890ff', color: '#fff', fontSize: 7 },
          { text: 'Establecimiento',        bold: true, fillColor: '#1890ff', color: '#fff', fontSize: 7 },
          { text: 'Nombre Comercial',       bold: true, fillColor: '#1890ff', color: '#fff', fontSize: 7 },
          { text: 'Cantidad Anual',         bold: true, fillColor: '#1890ff', color: '#fff', fontSize: 7 },
          { text: 'Unidad de Medida',       bold: true, fillColor: '#1890ff', color: '#fff', fontSize: 7 },
        ]
      ];

      (filteredData || []).forEach(s => {
        sustanciasBody.push([
          { text: str(s.IdSustanciaDeclarada), fontSize: 7 },
          { text: str(Utils.getIdDesc(s.IdSustancia, sustancias)), fontSize: 7 },
          { text: str(Utils.getIdDesc(s.IdEstablecimientoEmpresa, establecimientosEmpresa)), fontSize: 7 },
          { text: str(s.NombreComercial), fontSize: 7 },
          { text: str(s.CantidadAnual), fontSize: 7 },
          { text: str(Utils.getIdDesc(s.IdUnidadDeMedida, unidadesMedida)), fontSize: 7 },
        ]);
      });

      // ---- Secciones de detalle por sustancia ----
      const detalleSections = [];

      (filteredData || []).forEach(s => {
        const tieneDatos =
          (s.Utilizaciones?.length > 0) ||
          (s.PuestosAfectados?.length > 0) ||
          (s.EquiposRadiologicos?.length > 0) ||
          (s.Proveedores?.length > 0) ||
          (s.Compradores?.length > 0) ||
          (s.EstudiosAmbientalesEspecificos?.length > 0) ||
          (s.EstudiosBiologicosEspecificos?.length > 0);

        if (!tieneDatos) return;

        const establecimiento = getEstablecimiento(s.IdEstablecimientoEmpresa);
        const puestos = establecimiento?.Puestos || [];

        // Título de la sección
        detalleSections.push({
          text: `Detalle - Sustancia ${s.IdSustanciaDeclarada ?? ''} | ${str(Utils.getIdDesc(s.IdSustancia, sustancias))} | ${str(s.NombreComercial)}`,
          bold: true,
          fontSize: 10,
          margin: [0, 14, 0, 4],
          decoration: 'underline',
        });

        // --- Utilizaciones ---
        if (s.Utilizaciones?.length > 0) {
          detalleSections.push(sectionTitle('Utilizaciones'));
          detalleSections.push(subTable(
            ['Id', 'Uso Origen', 'Modo de Empleo'],
            s.Utilizaciones.map(u => [
              { text: str(u.IdUtilizacion), fontSize: 6 },
              { text: str(u.UsoOrigen), fontSize: 6 },
              { text: str(u.ModoDeEmpleo), fontSize: 6 },
            ]),
            ['auto', '*', '*']
          ));
        }

        // --- Puestos Afectados ---
        if (s.PuestosAfectados?.length > 0) {
          detalleSections.push(sectionTitle('Puestos Afectados'));
          detalleSections.push(subTable(
            ['Id', 'Puesto', 'Desc. Actividad', 'Inf. Riesgos', 'Cap. Riesgos', 'Entrega EPP', 'Desc. Estudios', 'Lic. Especial'],
            s.PuestosAfectados.map(p => [
              { text: str(p.IdPuestoAfectado), fontSize: 6 },
              { text: str(Utils.getIdDesc(p.IdPuesto, puestos)), fontSize: 6 },
              { text: str(p.DescripcionActividad), fontSize: 6 },
              { text: bool(p.InformaSobreRiesgos), fontSize: 6 },
              { text: bool(p.CapacitacionSobreRiesgos), fontSize: 6 },
              { text: bool(p.EntregaElementosDeProteccion), fontSize: 6 },
              { text: str(p.DescripcionEstudios), fontSize: 6 },
              { text: bool(p.LicenciaEspecial), fontSize: 6 },
            ]),
            ['auto', '*', '*', 'auto', 'auto', 'auto', '*', 'auto']
          ));

          // Medidas preventivas y EPP por puesto
          s.PuestosAfectados.forEach(p => {
            if (p.MedidasPreventivasDelPuesto?.length > 0) {
              detalleSections.push(sectionTitle(`  Medidas Preventivas - Puesto ${p.IdPuestoAfectado}`));
              detalleSections.push(subTable(
                ['Id', 'Referencial de Medidas Preventivas'],
                p.MedidasPreventivasDelPuesto.map(m => [
                  { text: str(m.IdMedidaPreventiva), fontSize: 6 },
                  { text: str(m.IdReferencialDeMedidasPreventivas), fontSize: 6 },
                ]),
                ['auto', '*']
              ));
            }
            if (p.ElementosProteccionPersonal?.length > 0) {
              detalleSections.push(sectionTitle(`  Elementos de Proteccion Personal - Puesto ${p.IdPuestoAfectado}`));
              detalleSections.push(subTable(
                ['Id', 'Referencial de Elemento de Proteccion'],
                p.ElementosProteccionPersonal.map(e => [
                  { text: str(e.IdElementoDeProteccion), fontSize: 6 },
                  { text: str(e.IdReferencialDeElementoDeProteccion), fontSize: 6 },
                ]),
                ['auto', '*']
              ));
            }
          });
        }

        // --- Equipos Radiologicos ---
        if (s.EquiposRadiologicos?.length > 0) {
          detalleSections.push(sectionTitle('Equipos Radiologicos'));
          detalleSections.push(subTable(
            ['Id', 'Marca', 'Modelo', 'Tipo Equipo', 'Caract. Equipo', 'Tipo Radiacion', 'Actividad', 'Radioisotopo', 'Tipo Fuente', 'KVP', 'mAp', 'MeV', 'Proc. Img.', 'Observaciones'],
            s.EquiposRadiologicos.map(e => [
              { text: str(e.IdEquipoRadiologico), fontSize: 6 },
              { text: str(e.Marca), fontSize: 6 },
              { text: str(e.Modelo), fontSize: 6 },
              { text: str(e.IdTipoEquipo), fontSize: 6 },
              { text: str(e.IdCaracteristicaEquipo), fontSize: 6 },
              { text: str(e.IdTipoRadiacion), fontSize: 6 },
              { text: str(e.Actividad), fontSize: 6 },
              { text: str(e.RadioisotopoFuente), fontSize: 6 },
              { text: str(e.IdTipoFuente), fontSize: 6 },
              { text: str(e.PotenciaEquipoKVP), fontSize: 6 },
              { text: str(e.PotenciaEquipomAp), fontSize: 6 },
              { text: str(e.PotenciaEquipoMeV), fontSize: 6 },
              { text: bool(e.ProcesamientoImagenes), fontSize: 6 },
              { text: str(e.Observaciones), fontSize: 6 },
            ]),
            ['auto', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto', '*']
          ));

          // Sub-tablas de cada equipo
          s.EquiposRadiologicos.forEach(e => {
            // Dosimetria
            if (e.Dosimetria) {
              detalleSections.push(sectionTitle(`  Dosimetria - Equipo ${e.IdEquipoRadiologico}`));
              detalleSections.push(subTable(
                ['Id', 'Cant. Personal', 'Tipo Dosimetria', 'Metodo Dosimetria', 'Cuit Responsable'],
                [[
                  { text: str(e.Dosimetria.IdDosimetria), fontSize: 6 },
                  { text: str(e.Dosimetria.CantidadDePersonal), fontSize: 6 },
                  { text: str(e.Dosimetria.TipoDosimetria), fontSize: 6 },
                  { text: str(e.Dosimetria.MetodoDosimetria), fontSize: 6 },
                  { text: str(e.Dosimetria.CuitResponsable), fontSize: 6 },
                ]],
                ['auto', 'auto', '*', '*', 'auto']
              ));
            }
            // Responsables de Uso
            if (e.ResponsablesDeUso?.length > 0) {
              detalleSections.push(sectionTitle(`  Responsables de Uso - Equipo ${e.IdEquipoRadiologico}`));
              detalleSections.push(subTable(
                ['Id', 'CUIL', 'Autorizacion Individual', 'Nro Permiso', 'Matricula Prof.', 'F. Expedicion', 'F. Vencimiento'],
                e.ResponsablesDeUso.map(r => [
                  { text: str(r.IdResponsableDeUso), fontSize: 6 },
                  { text: str(r.Cuil), fontSize: 6 },
                  { text: str(r.AutorizacionIndividual), fontSize: 6 },
                  { text: str(r.NroPermisoHabilitante), fontSize: 6 },
                  { text: str(r.MatriculaProfesional), fontSize: 6 },
                  { text: parser.date(r.FechaExpedicion), fontSize: 6 },
                  { text: parser.date(r.FechaVencimiento), fontSize: 6 },
                ]),
                ['auto', 'auto', '*', 'auto', 'auto', 'auto', 'auto']
              ));
            }
            // Responsables de Instalacion
            if (e.ResponsablesDeInstalacion?.length > 0) {
              detalleSections.push(sectionTitle(`  Responsables de Instalacion - Equipo ${e.IdEquipoRadiologico}`));
              detalleSections.push(subTable(
                ['Id', 'CUIL', 'Nro Licencia', 'Domicilio Hab.', 'F. Expedicion', 'F. Vencimiento'],
                e.ResponsablesDeInstalacion.map(r => [
                  { text: str(r.IdResponsableDeInstalacion), fontSize: 6 },
                  { text: str(r.Cuil), fontSize: 6 },
                  { text: str(r.NroLicenciaHabilitante), fontSize: 6 },
                  { text: str(r.DomicilioHabilitacion), fontSize: 6 },
                  { text: parser.date(r.FechaExpedicion), fontSize: 6 },
                  { text: parser.date(r.FechaVencimiento), fontSize: 6 },
                ]),
                ['auto', 'auto', 'auto', '*', 'auto', 'auto']
              ));
            }
            // Encargados de Proteccion Radiologica
            if (e.EncargadosDeProteccionRadiologica?.length > 0) {
              detalleSections.push(sectionTitle(`  Encargados de Proteccion Radiologica - Equipo ${e.IdEquipoRadiologico}`));
              detalleSections.push(subTable(
                ['Id', 'CUIL', 'Titulo Profesional', 'Matricula Prof.', 'Nro Habilitacion'],
                e.EncargadosDeProteccionRadiologica.map(r => [
                  { text: str(r.IdEncargadoDeProteccionRadiologica), fontSize: 6 },
                  { text: str(r.Cuil), fontSize: 6 },
                  { text: str(r.TituloProfesional), fontSize: 6 },
                  { text: str(r.MatriculaProfesional), fontSize: 6 },
                  { text: str(r.NroHabilitacion), fontSize: 6 },
                ]),
                ['auto', 'auto', '*', 'auto', 'auto']
              ));
            }
          });
        }

        // --- Proveedores ---
        if (s.Proveedores?.length > 0) {
          detalleSections.push(sectionTitle('Proveedores'));
          detalleSections.push(subTable(
            ['Id', 'CUIT', 'Nombre Comercial'],
            s.Proveedores.map(p => [
              { text: str(p.IdProveedor), fontSize: 6 },
              { text: str(p.Cuit), fontSize: 6 },
              { text: str(p.NombreComercial), fontSize: 6 },
            ]),
            ['auto', 'auto', '*']
          ));
        }

        // --- Compradores ---
        if (s.Compradores?.length > 0) {
          detalleSections.push(sectionTitle('Compradores'));
          detalleSections.push(subTable(
            ['Id', 'CUIT', 'Nombre Comercial'],
            s.Compradores.map(c => [
              { text: str(c.IdComprador), fontSize: 6 },
              { text: str(c.Cuit), fontSize: 6 },
              { text: str(c.NombreComercial), fontSize: 6 },
            ]),
            ['auto', 'auto', '*']
          ));
        }

        // --- Estudios Ambientales Especificos ---
        if (s.EstudiosAmbientalesEspecificos?.length > 0) {
          detalleSections.push(sectionTitle('Estudios Ambientales Especificos'));
          detalleSections.push(subTable(
            ['Id', 'Cantidad Frecuencia', 'Unidad Frecuencia', 'Metodologia Empleada'],
            s.EstudiosAmbientalesEspecificos.map(e => [
              { text: str(e.IdEstudioAmbientalEspecifico), fontSize: 6 },
              { text: str(e.CantidadFrecuencia), fontSize: 6 },
              { text: str(e.IdUnidadFrecuencia), fontSize: 6 },
              { text: str(e.MetodologiaEmpleada), fontSize: 6 },
            ]),
            ['auto', 'auto', 'auto', '*']
          ));
        }

        // --- Estudios Biologicos Especificos ---
        if (s.EstudiosBiologicosEspecificos?.length > 0) {
          detalleSections.push(sectionTitle('Estudios Biologicos Especificos'));
          detalleSections.push(subTable(
            ['Id', 'Cantidad Frecuencia', 'Unidad Frecuencia', 'Analisis Estudios Complementacion'],
            s.EstudiosBiologicosEspecificos.map(e => [
              { text: str(e.IdEstudioBiologicoEspecifico), fontSize: 6 },
              { text: str(e.CantidadFrecuencia), fontSize: 6 },
              { text: str(e.IdUnidadFrecuencia), fontSize: 6 },
              { text: str(e.AnalisisEstudiosComplementacion), fontSize: 6 },
            ]),
            ['auto', 'auto', 'auto', '*']
          ));
        }
      });

      // ---- Documento ----
      const documentDefinition = {
        pageOrientation: 'landscape',
        pageMargins: [30, 50, 30, 30],
        content: [
          {
            text: 'Listado de Sustancias',
            bold: true,
            fontSize: 18,
            alignment: 'center',
            margin: [0, 0, 0, 5],
          },
          {
            columns: [
              { text: `CUIT: ${cuit || ''}`, fontSize: 10, alignment: 'left' },
              { text: `Fecha de generacion: ${formattedDate}`, fontSize: 10, alignment: 'right' },
            ],
            margin: [0, 0, 0, 10],
          },
          {
            canvas: [{ type: 'line', x1: 0, y1: 0, x2: 760, y2: 0, lineWidth: 0.5 }],
            margin: [0, 0, 0, 10],
          },
          {
            columns: [
              { text: 'Sustancias', bold: true, fontSize: 12 },
              { text: `Total: ${(filteredData || []).length}`, fontSize: 10, alignment: 'right', margin: [0, 2, 0, 0] },
            ],
            margin: [0, 0, 0, 5],
          },
          {
            table: {
              headerRows: 1,
              widths: ['auto', '*', '*', 'auto', 'auto', 'auto'],
              body: sustanciasBody,
            },
            layout: {
              hLineWidth: () => 0.5,
              vLineWidth: () => 0.5,
              hLineColor: () => '#cccccc',
              vLineColor: () => '#cccccc',
            },
          },
          ...detalleSections,
        ],
        footer: (currentPage, pageCount) => ({
          columns: [
            { text: `Pagina ${currentPage} de ${pageCount}`, alignment: 'left', fontSize: 8, margin: [30, 0, 0, 0] },
            { text: `Impreso: ${formattedDate}`, alignment: 'right', fontSize: 8, margin: [0, 0, 30, 0] },
          ],
        }),
      };

      pdfMake.createPdf(documentDefinition).download(`sustancias_${cuit || 'export'}.pdf`, () => {
        setLoading(false);
      });
    }, 0);
  };

  return (
    <Button
      onClick={downloadPDF}
      type="primary"
      icon={<FilePdfOutlined />}
      loading={loading}
    >
      Exportar PDF
    </Button>
  );
};

export default ExportSustanciasPDF;
